export interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
  fullName: string;
  company: string;
  jobTitle: string;
  userType: string;
  useCase: string;
  referralSource: string;
  country: string;
  phoneNumber: string;
  selectedRole: string;
  adminFallbackRole: string;
  termsAccepted: boolean;
  privacyAccepted: boolean;
  marketingConsent: boolean;
}

export interface SignupErrors {
  email: string;
  password: string;
  confirmPassword: string;
  fullName: string;
  company: string;
  jobTitle: string;
  userType: string;
  useCase: string;
  referralSource: string;
  country: string;
  phoneNumber: string;
  selectedRole: string;
  adminFallbackRole: string;
  termsAccepted: string;
  privacyAccepted: string;
  general: string;
}

export const emptySignupErrors = (): SignupErrors => ({
  email: '',
  password: '',
  confirmPassword: '',
  fullName: '',
  company: '',
  jobTitle: '',
  userType: '',
  useCase: '',
  referralSource: '',
  country: '',
  phoneNumber: '',
  selectedRole: '',
  adminFallbackRole: '',
  termsAccepted: '',
  privacyAccepted: '',
  general: '',
});

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
const phonePattern = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;

export const validateSignupField = (
  name: keyof SignupErrors,
  value: string | boolean,
  formData: SignupFormData,
): string => {
  if (name === 'email') {
    if (!value) return 'Email is required';
    if (typeof value === 'string' && !emailPattern.test(value)) return 'Please enter a valid email address';
  }
  if (name === 'password') {
    if (!value) return 'Password is required';
    if (typeof value === 'string' && value.length < 8) return 'Password must be at least 8 characters';
    if (typeof value === 'string' && !passwordPattern.test(value)) {
      return 'Password must contain uppercase, lowercase, number, and special character';
    }
  }
  if (name === 'confirmPassword') {
    if (!value) return 'Please confirm your password';
    if (value !== formData.password) return 'Passwords do not match';
  }
  if (name === 'fullName') {
    if (!value || String(value).trim() === '') return 'Full name is required';
    if (String(value).trim().length < 2) return 'Please enter your full name';
  }
  if (name === 'company' && (!value || String(value).trim() === '')) return 'Company/Organization is required';
  if (name === 'jobTitle' && (!value || String(value).trim() === '')) return 'Job title is required';
  if (name === 'userType') {
    // Invite testers cannot pick a profile type. General is locked on submit.
    return '';
  }
  if (name === 'useCase' && !value) return 'Please select your primary use case';
  if (name === 'referralSource' && !value) return 'Please tell us how you heard about VerdeKai';
  if (name === 'country' && !value) return 'Please select your country';
  if (name === 'phoneNumber') {
    if (!value || String(value).trim() === '') return '';
    if (!phonePattern.test(String(value))) return 'Please enter a valid phone number';
  }
  if (name === 'selectedRole' || name === 'adminFallbackRole') {
    // Invite testers cannot pick a role. Analyst is locked on submit.
    return '';
  }
  if (name === 'termsAccepted' && !value) return 'You must accept the Terms of Service';
  if (name === 'privacyAccepted' && !value) return 'You must accept the Privacy Policy';
  return '';
};

export const validateSignupForm = (formData: SignupFormData): SignupErrors => ({
  ...emptySignupErrors(),
  email: validateSignupField('email', formData.email, formData),
  password: validateSignupField('password', formData.password, formData),
  confirmPassword: validateSignupField('confirmPassword', formData.confirmPassword, formData),
  fullName: validateSignupField('fullName', formData.fullName, formData),
  company: validateSignupField('company', formData.company, formData),
  jobTitle: validateSignupField('jobTitle', formData.jobTitle, formData),
  userType: validateSignupField('userType', formData.userType, formData),
  useCase: validateSignupField('useCase', formData.useCase, formData),
  referralSource: validateSignupField('referralSource', formData.referralSource, formData),
  country: validateSignupField('country', formData.country, formData),
  phoneNumber: validateSignupField('phoneNumber', formData.phoneNumber, formData),
  selectedRole: validateSignupField('selectedRole', formData.selectedRole, formData),
  adminFallbackRole: validateSignupField('adminFallbackRole', formData.adminFallbackRole, formData),
  termsAccepted: validateSignupField('termsAccepted', formData.termsAccepted, formData),
  privacyAccepted: validateSignupField('privacyAccepted', formData.privacyAccepted, formData),
});
